import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import {
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  combineLatest,
} from 'rxjs';

import { DerivedAuthService, IconComponent } from '@shared/public-api';

import {
  IHoldMessage,
  HoldsService,
  ROUTE_DISPLAY_MAP,
} from '../../services/holds.service';

@Component({
  selector: 'wu-holds-notification',
  imports: [NgClass, IconComponent],

  templateUrl: './holds-notification.component.html',
})
export class HoldsNotificationComponent implements OnDestroy {
  private readonly holdsService = inject(HoldsService);
  private readonly derivedAuthService = inject(DerivedAuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private intervalId!: ReturnType<typeof setInterval>;

  currentIndex = signal(0);
  holdMessages = signal<IHoldMessage[]>([]);

  currentMessage = computed(
    () =>
      this.holdMessages()[this.currentIndex()] ?? {
        title: '',
        description: '',
      },
  );

  anyHoldsHaveButton = computed(() =>
    this.holdMessages().some(h => h.showButton),
  );

  constructor() {
    const bannerId$ = toObservable(this.derivedAuthService.studentId).pipe(
      filter((id): id is string => !!id),
    );

    const allHolds$ = bannerId$.pipe(
      switchMap(bannerId => this.holdsService.getAllHolds(bannerId)),
      shareReplay(1),
    );

    const routeUrl$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
      startWith(this.router.url),
    );

    // Wait for both holds and route before filtering
    combineLatest([allHolds$, routeUrl$])
      .pipe(
        map(([holds, url]) => {
          const routeSegment = url.split('/')[1]?.split('?')[0] ?? '';
          const displayLocation = ROUTE_DISPLAY_MAP[routeSegment];
          return displayLocation
            ? this.holdsService.filterByLocation(holds, displayLocation)
            : [];
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: holds => {
          this.holdMessages.set(holds);
          this.resetCarousel();
        },
        error: err => console.error('Failed to load holds:', err),
      });
  }

  /**
   * Resets the carousel to the first slide and (re)starts the auto-advance
   * interval. The interval is only started when there are multiple holds to
   * cycle through; otherwise the timer is left cleared.
   */
  private resetCarousel(): void {
    this.currentIndex.set(0);
    clearInterval(this.intervalId);
    if (this.holdMessages().length > 1) {
      this.intervalId = setInterval(() => this.nextSlide(), 7000);
    }
  }

  /** Clears the auto-advance interval when the component is destroyed. */
  ngOnDestroy(): void {
    clearInterval(this.intervalId);
  }

  /** Advances the carousel to the next hold, wrapping back to the first after the last. */
  nextSlide(): void {
    this.currentIndex.update(index => (index + 1) % this.holdMessages().length);
  }

  /** Jumps the carousel directly to the hold at the given zero-based `index`. */
  goToSlide(index: number): void {
    this.currentIndex.set(index);
  }

  /** Opens the action URL of the currently displayed hold in a new browser tab. */
  openMessage(): void {
    const url = (this.currentMessage() as any)?.url;
    if (url) {
      window.open(url, '_blank');
    }
  }
}
